import { KeyboardEvent } from "react";
import { Money } from "./Money";
import { getNumber } from "./validation";
import { cx } from "class-variance-authority";
import { DroppsVariant } from "@/store";

export const cn = cx;

export function check<T>(t: T | undefined | null, msg?: string): T {
  if (t === undefined || t === null) {
    throw new Error(msg ?? "unexpected null or undefined");
  } else {
    return t;
  }
}

export function getCurrency(price: number): string {
  return Money.from(price, "$").format();
}

export function percentToDecimal(percent: string): number {
  return parseFloat(percent) / 100.0;
}
export const scrollToId = (id: string) => {
  const target: HTMLElement | null | "" =
    id && document.querySelector<HTMLElement>(id);
  if (target) {
    target.scrollIntoView();
    target.focus({ preventScroll: true });
    if (document.activeElement !== target) {
      target.setAttribute("tabindex", "-1"); // Adding tabindex for elements not focusable
      target.focus({ preventScroll: true }); // Setting focus
    }
  }
};

export const getRandomInt = (max: number) => {
  return Math.floor(Math.random() * max);
};

export const pluralize = (count: number, word: string) => {
  if (count !== 1) return word + "s";
  else return word;
};

export const addMonthsToDate = (date: Date, months: number) => {
  const newDate = new Date(date);
  const d = newDate.getDate();
  newDate.setMonth(newDate.getMonth() + +months);
  if (newDate.getDate() != d) {
    newDate.setDate(0);
  }
  return newDate;
};

export const addDaysToDate = (date: Date, days: number) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const getQueryParam = (param: string): string | null => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(param);
};

const sizeRe = /(.+?)(_\d*x\d*)?(\.\w+)(([?#].*)|$)/;
export const getSizedImage = (imgUrl: string, size: string) => {
  return imgUrl.replace(sizeRe, `$1_${size}$3$4`);
};

export const getDiscountPercent = (
  basePrice: number,
  subscriptionPrice: number
): number => Math.floor((1 - subscriptionPrice / basePrice) * 100);

export function keyboardTrigger(
  fn: () => void,
  key: "Space" | "Enter" | undefined = "Enter"
) {
  const k = key == "Space" ? " " : key;
  return (e: KeyboardEvent) => {
    if (e.key === k && !e.isPropagationStopped()) {
      e.preventDefault();
      e.stopPropagation();
      fn();
    }
  };
}

export function getWindowPositioning(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX,
    right: rect.right + window.scrollX,
    top: rect.top + window.scrollY,
    bottom: rect.bottom + window.scrollY,
  };
}

export function groupBy<T, K>(items: T[], fn: (arg0: T) => K): Map<K, T[]> {
  const result: Map<K, T[]> = new Map();
  for (const item of items) {
    const key = fn(item);
    const existing = result.get(key);
    result.set(key, [...(existing ?? []), item]);
  }
  return result;
}

export function getIsoDateFormat(date: Date) {
  const offset = date.getTimezoneOffset();
  const outputDate = new Date(date.getTime() - offset * 60 * 1000);
  return outputDate.toISOString().split("T")[0];
}

export const clamp = (value: number, min: number, max: number) => {
  return Math.max(min, Math.min(max, value));
};

export const calculateBundleValue = (variants: DroppsVariant[]) => {
  const retailValue = variants.reduce((acc, x) => acc.add(x.price), Money.zero);
  const subs = variants.map((x) => x.subscription?.discount.price ?? x.price);
  const subValue = subs.reduce((acc, x) => acc.add(x), Money.zero);
  return { retailValue, subscriptionRetailValue: subValue };
};
